// stores/translation.js
import { defineStore } from 'pinia';
import axios from '../../axios';

// Keep <html lang> in sync with the active language (SEO + screen readers).
function applyHtmlLang(lang) {
  if (typeof document === 'undefined' || !document.documentElement) return;
  document.documentElement.lang = String(lang || 'en').startsWith('zh') ? 'zh-Hans' : 'en-AU';
}

const TR_CACHE_KEY = 'chds_translations_v1';

export const useTranslationStore = defineStore('translation', {
  state: () => ({
    translations: [],            
    modules: {},                 
    currentLanguage: ((typeof localStorage !== 'undefined' && localStorage.getItem('chds_lang'))
      || ((typeof navigator !== 'undefined' && (navigator.language || '').toLowerCase().startsWith('zh')) ? 'zh' : 'en')),
    isLoaded: false,             
    isLoading: false,            
    loadError: null,             
  }),
  actions: {

    async fetchTranslations() {
      applyHtmlLang(this.currentLanguage);
      if (this.isLoaded) {
        return Promise.resolve();
      }

      if (this.isLoading) {
        return new Promise((resolve, reject) => {
          const checkLoaded = () => {
            if (this.isLoaded) {
              resolve();
            } else if (this.loadError) {
              reject(this.loadError);
            } else {
              setTimeout(checkLoaded, 100); 
            }
          };
          checkLoaded();
        });
      }

      this.isLoading = true;
      this.loadError = null;

      // Start from the last good copy on this phone: labels show instantly, and a failed
      // download on weak mobile data (WeChat's in-app browser) never leaves raw keys like
      // "full_name" on screen for the whole visit.
      if (!(this.translations || []).length) {
        try {
          const c = JSON.parse(localStorage.getItem(TR_CACHE_KEY) || 'null');
          if (c && Array.isArray(c.rows) && c.rows.length) { this.translations = c.rows; this.organizeTranslations(); }
        } catch (e) { /* storage unavailable */ }
      }
      try {
        let lastErr = null;
        for (const wait of [0, 1200, 3500]) {          // retry twice before giving up
          if (wait) await new Promise((r) => setTimeout(r, wait));
          try {
            const response = await axios.get('/translations/all');
            this.translations = response.data;
            this.organizeTranslations();
            this.isLoaded = true;
            try { localStorage.setItem(TR_CACHE_KEY, JSON.stringify({ at: Date.now(), rows: response.data })); } catch (e) { /* full / private mode */ }
            return;
          } catch (e) { lastErr = e; }
        }
        if ((this.translations || []).length) { this.isLoaded = true; return; }   // stale copy beats raw keys
        console.error('Error fetching translations:', lastErr);
        this.loadError = lastErr;
        throw lastErr;
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Organize translations by module and language for efficient access.
     */
    organizeTranslations() {
      this.modules = {};

      this.translations.forEach((item) => {
        if (!item.module || !item.module.feature_code) return; // skip ungrouped rows
        const { feature_code } = item.module;
        const { language, label, value } = item;

        if (!this.modules[feature_code]) {
          this.modules[feature_code] = {};
        }

        if (!this.modules[feature_code][language]) {
          this.modules[feature_code][language] = {};
        }

        this.modules[feature_code][language][label] = value;
      });
    },
    /**
     * Set the current language.
     * @param {string} lang - The language code to set (e.g., 'en', 'zh').
     */
    setLanguage(lang) {
      this.currentLanguage = lang;
      try { localStorage.setItem('chds_lang', lang); } catch (e) { /* storage unavailable */ }
      applyHtmlLang(lang);
    },
    /**
     * Optional: Force reload translations, bypassing the cache.
     */
    async reloadTranslations() {
      this.isLoaded = false;
      await this.fetchTranslations();
    },
  },
  getters: {
    /**
     * Get all translations for the specified modules based on the current language.
     * @param {Array<string>} moduleCodes - Array of module feature codes.
     * @returns {Object} Combined translations from the specified modules.
     */
    getModuleTranslations: (state) => {
      return (moduleCodes) => {
        let combinedTranslations = {};

        moduleCodes.forEach((code) => {
          const moduleTranslations = state.modules[code]?.[state.currentLanguage];
          if (moduleTranslations) {
            combinedTranslations = { ...combinedTranslations, ...moduleTranslations };
          }
        });

        return combinedTranslations;
      };
    },
    /**
     * Retrieve a specific translation for a given label.
     * Accepts multiple module codes to handle labels present in multiple modules.
     * @param {string} label - The label key to translate.
     * @param {Array<string>} moduleCodes - Array of module feature codes.
     * @returns {string} The translated value or the label itself if not found.
     */
    translate: (state) => {
      return (label, moduleCodes = [], fallback = null) => {
        let translation = '';
        // Current language — check ALL given modules (not just the first) so a label
        // whose zh value lives in a later module still resolves.
        moduleCodes.forEach((code) => {
          if (translation) return;
          const m = state.modules[code]?.[state.currentLanguage];
          if (m && m[label]) translation = m[label];
        });
        if (!translation) {
          // Fall back to English when the current-language value is missing.
          moduleCodes.forEach((code) => {
            if (translation) return;
            const m = state.modules[code]?.['en'];
            if (m && m[label]) translation = m[label];
          });
        }
        if (translation) return translation;
        // Missing entirely: use the caller's fallback (e.g. a dish's own name) when
        // given; otherwise the label itself (unchanged behaviour for UI strings).
        return fallback != null ? fallback : label;
      };
    },
    /**
     * Get sorted translations for specified modules.
     * @param {Array<string>} moduleCodes - Array of module feature codes.
     * @returns {Object} Sorted translations object.
     */
    getSortedModuleLabels: (state, getters) => {
      return (moduleCodes) => {
        const translations = getters.getModuleTranslations(moduleCodes);
        return Object.keys(translations)
          .sort()
          .reduce((acc, key) => {
            acc[key] = translations[key];
            return acc;
          }, {});
      };
    },
    getLanguageCode() {
      // Retrieve the browser's language setting
      const browserLanguage = navigator.language || navigator.userLanguage;
      console.log(browserLanguage);
      // Check if the language starts with 'zh' (covers all Chinese variants)
      if (browserLanguage.toLowerCase().startsWith('zh')) {
        return 'zh';
      } else {
        return 'en';
      }
    },
  },
});
